import { BOX_TRUCK_DEFAULT_MAX_POSTED_AGE_HOURS } from '@numeo/types-core'
import type { BoxTruckLoadType } from '../hooks/requestMapping'
import { EMPTY_REFINEMENTS, type BoxTruckRefinements } from '../components/BoxTruckRefinementBar'

/** The refinement keys owned by the Advanced Filters panel; min/max trip miles remain on the main row. */
const ADVANCED_REFINEMENT_KEYS = ['minR', 'minT', 'minWeight', 'maxWeight', 'contact'] as const

export interface AdvancedSearchValues {
    /** 'BOTH' = unset. Sent with the search request, not applied to the loaded rows. */
    fullPartial: BoxTruckLoadType
    lengthFt: number | null
    weightLbs: number | null
    /** The request's own `maxPostedAgeHours`. Always set — the control offers no "All". */
    maxPostedAgeHours: number
}

/**
 * What a board starts with: nothing, on every board.
 *
 * A search tab used to open on a one-hour Max Age (#12142). That cap measured ARRIVAL, while the page the
 * server had already chosen was selected by the broker's POSTED claim, and on this feed the two disagree by
 * hours. Measured on prod 2026-10-05, anywhere-to-anywhere: 545 rows matched, the server returned the 150
 * newest by claim, 226 loads had arrived in the last hour and only 35 of them were on that page — so the cap
 * hid most of what was left. With an origin state the matching set fits in a single page, which is exactly
 * why the board only ever looked broken for anywhere searches.
 *
 * This stays a function rather than a constant because the cap comes back: once it is the request's own
 * `maxPostedAgeHours`, applied by the server over the whole matching set rather than by the client over a
 * page chosen on a different clock, this is where a board says what it opens with.
 */
export const defaultRefinementsForBoard = (): BoxTruckRefinements => EMPTY_REFINEMENTS

/** Clear the refinements the Advanced Filters panel owns. */
export const clearAdvancedRefinements = (refinements: BoxTruckRefinements): BoxTruckRefinements => {
    const cleared = { ...refinements }
    for (const key of ADVANCED_REFINEMENT_KEYS) cleared[key] = ''
    return cleared
}

/**
 * Single active-count rule shared by the Advanced Filters badge and compact-board summary.
 *
 * `defaults` is the board's own starting point, so the one-hour age a search tab OPENS on is not reported as
 * a filter the operator applied. A value counts only when it is both set and different from that default:
 * widening the age to All narrows nothing, so it earns no badge either.
 */
export const countActiveAdvanced = (
    search: AdvancedSearchValues,
    refinements: BoxTruckRefinements,
    defaults: BoxTruckRefinements,
    /** `countMaxAge: false` for a board that does not SHOW the Max Age control — Driver Bids. A badge there
     *  would promise a filter the view neither applies nor offers any way to clear. */
    { countMaxAge = true }: { countMaxAge?: boolean } = {}
): number => {
    let count = 0
    if (search.fullPartial !== 'BOTH') count++
    // The age the board OPENS on is not a filter the operator applied, so only a changed one is badged —
    // the same rule the refinements below follow.
    if (countMaxAge && search.maxPostedAgeHours !== BOX_TRUCK_DEFAULT_MAX_POSTED_AGE_HOURS) count++
    if (search.lengthFt != null) count++
    if (search.weightLbs != null) count++
    for (const key of ADVANCED_REFINEMENT_KEYS) if (refinements[key] !== '' && refinements[key] !== defaults[key]) count++
    return count
}

/**
 * Clear one source chip on EVERY board.
 *
 * Switching the DAT feed on is a request FOR its rows, so a chip still hiding them must not silently swallow
 * the feed the operator just asked for: they flip the switch, the rows arrive, the chip drops them, and the
 * board looks unchanged for a reason that is one collapsed panel away from view.
 *
 * Every board rather than only the active one, because the switch governs what every board REQUESTS. The
 * others re-search on their next visit — their snapshots are dropped when it is switched on — and would meet
 * the same silent hide there.
 */
export const unhideSourceOnEveryBoard = (hiddenByBoard: Record<string, Set<string>>, source: string): Record<string, Set<string>> => {
    const affected = Object.keys(hiddenByBoard).filter((board) => hiddenByBoard[board].has(source))
    if (affected.length === 0) return hiddenByBoard
    const next = { ...hiddenByBoard }
    for (const board of affected) {
        const without = new Set(next[board])
        without.delete(source)
        next[board] = without
    }
    return next
}
